import React, { useEffect } from 'react';
import { usePageContext } from '../context/PageContext';

const HowItWorks = () => {
  const { setPageTitle } = usePageContext();

  useEffect(() => {
    setPageTitle('How It Works');
  }, [setPageTitle]);
  return (
    <div>
      <div className="mb-4">
        <p className="text-muted mb-0">Complete guide to understanding and using the payroll system</p>
      </div>
          
      {/* System Overview */}
      <div className="card mb-4">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #007bff, #0056b3)', color: 'white' }}>
          <h3 className="card-title" style={{ color: 'white', margin: 0 }}>System Overview</h3>
        </div>
            <div className="card-body">
              <p className="lead">
                This payroll system replaces Excel-based calculations with automated, condition-based payroll processing.
                The system uses <strong>Payroll Conditions</strong> to determine how much Caliber earns (margin) and how much consultants get paid.
              </p>
            </div>
          </div>

      {/* Key Concepts */}
      <div className="card mb-4">
        <div className="card-header" style={{ background: 'linear-gradient(135deg, #17a2b8, #138496)', color: 'white' }}>
          <h3 className="card-title" style={{ color: 'white', margin: 0 }}>Payroll Conditions - The Complete System</h3>
        </div>
            <div className="card-body">
              <div className="alert alert-success">
                <i className="fas fa-check-circle me-2"></i>
                <strong>Single Unified System:</strong> We use ONE comprehensive "Payroll Conditions" system that covers all calculation needs.
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <h5 className="text-primary">🧮 Calculation Types</h5>
                  <ul>
                    <li><strong>Margin Percentage:</strong> 25% of $100 client rate = $25/hour margin</li>
                    <li><strong>Flat Rate:</strong> Fixed $50/hour margin regardless of client rate</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5 className="text-success">📊 Advanced Features</h5>
                  <ul>
                    <li><strong>Margin Tiers:</strong> Different rates for different hour ranges</li>
                    <li><strong>Bonus Structure:</strong> Performance-based bonuses</li>
                    <li><strong>Hike Structure:</strong> Pay increases after hour thresholds</li>
                  </ul>
                </div>
              </div>
              
              <div className="row mt-3">
                <div className="col-md-4">
                  <div className="card" style={{ border: '1px solid #007bff' }}>
                    <div className="card-body text-center">
                      <h6 className="text-primary">Flat Rate Example</h6>
                      <p className="mb-0">$50/hour for 0-500 hours<br />$40/hour for 500+ hours</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card" style={{ border: '1px solid #28a745' }}>
                    <div className="card-body text-center">
                      <h6 className="text-success">Percentage Example</h6>
                      <p className="mb-0">25% margin for 0-500 hours<br />20% margin for 500+ hours</p>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card" style={{ border: '1px solid #ffc107' }}>
                    <div className="card-body text-center">
                      <h6 className="text-warning">Combined Example</h6>
                      <p className="mb-0">Flat rate + $5/hour hike<br />after 1000 hours</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* System Flow Diagram */}
          <div className="card mb-4">
            <div className="card-header bg-success text-white">
              <h3 className="mb-0">System Flow Diagram</h3>
            </div>
            <div className="card-body">
              <div className="text-center mb-4">
                <div className="row">
                  <div className="col-md-2">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <i className="fas fa-user fa-2x text-primary mb-2"></i>
                        <h6>Employee</h6>
                        <small>Has client rate & consultant rate</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-1 d-flex align-items-center justify-content-center">
                    <i className="fas fa-arrow-right fa-2x text-muted"></i>
                  </div>
                  <div className="col-md-2">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <i className="fas fa-calculator fa-2x text-info mb-2"></i>
                        <h6>Payroll Condition</h6>
                        <small>Assigned to employee</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-1 d-flex align-items-center justify-content-center">
                    <i className="fas fa-arrow-right fa-2x text-muted"></i>
                  </div>
                  <div className="col-md-2">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <i className="fas fa-clock fa-2x text-warning mb-2"></i>
                        <h6>Monthly Hours</h6>
                        <small>Timesheet submission</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-1 d-flex align-items-center justify-content-center">
                    <i className="fas fa-arrow-right fa-2x text-muted"></i>
                  </div>
                  <div className="col-md-2">
                    <div className="card bg-light">
                      <div className="card-body p-2">
                        <i className="fas fa-file-invoice fa-2x text-success mb-2"></i>
                        <h6>Auto Invoice</h6>
                        <small>Generated with calculations</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Calculation Examples */}
          <div className="card mb-4">
            <div className="card-header bg-dark text-white">
              <h3 className="mb-0">Calculation Examples</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-lg-4 mb-4">
                  <div className="card border-primary">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">📊 Percentage-Based</h5>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled mb-3">
                        <li><strong>Employee:</strong> John Doe</li>
                        <li><strong>Client Rate:</strong> $120/hour</li>
                        <li><strong>Consultant Rate:</strong> $100/hour</li>
                        <li><strong>Hours Worked:</strong> 600 hours</li>
                        <li><strong>Condition:</strong> 20% of consultant rate</li>
                      </ul>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            <tr><td>Client Invoice</td><td className="text-end"><strong>$72,000</strong></td></tr>
                            <tr><td>Consultant Invoice</td><td className="text-end"><strong>$60,000</strong></td></tr>
                            <tr className="table-warning"><td>Caliber Margin</td><td className="text-end"><strong>$12,000</strong></td></tr>
                            <tr><td>Consultant Pay</td><td className="text-end"><strong>$48,000</strong></td></tr>
                            <tr className="table-success"><td>Caliber Profit</td><td className="text-end"><strong>$24,000</strong></td></tr>
                          </tbody>
                        </table>
                      </div>
                      <small className="text-muted">Calculation: 600 × $100 × 20% = $12,000 margin</small>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-4 mb-4">
                  <div className="card border-success">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">💰 Flat Rate</h5>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled mb-3">
                        <li><strong>Employee:</strong> Sarah Wilson</li>
                        <li><strong>Client Rate:</strong> $120/hour</li>
                        <li><strong>Consultant Rate:</strong> $100/hour</li>
                        <li><strong>Hours Worked:</strong> 600 hours</li>
                        <li><strong>Condition:</strong> $10 (0-500hrs), $35 (500-1000hrs)</li>
                      </ul>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            <tr><td>Client Invoice</td><td className="text-end"><strong>$72,000</strong></td></tr>
                            <tr><td>Pay for 0-500 hrs</td><td className="text-end">$5,000</td></tr>
                            <tr><td>Pay for 500-600 hrs</td><td className="text-end">$3,500</td></tr>
                            <tr className="table-info"><td>Total Consultant Pay</td><td className="text-end"><strong>$8,500</strong></td></tr>
                            <tr className="table-success"><td>Caliber Profit</td><td className="text-end"><strong>$63,500</strong></td></tr>
                          </tbody>
                        </table>
                      </div>
                      <small className="text-muted">Calculation: (500×$10) + (100×$35) = $8,500 consultant pay</small>
                    </div>
                  </div>
                </div>
                
                <div className="col-lg-4 mb-4">
                  <div className="card border-info">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">🔄 Mixed</h5>
                    </div>
                    <div className="card-body">
                      <ul className="list-unstyled mb-3">
                        <li><strong>Employee:</strong> Mike Johnson</li>
                        <li><strong>Client Rate:</strong> $120/hour</li>
                        <li><strong>Consultant Rate:</strong> $100/hour</li>
                        <li><strong>Hours Worked:</strong> 600 hours</li>
                        <li><strong>Condition:</strong> $35 (0-500hrs), 70% (500-1000hrs)</li>
                      </ul>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            <tr><td>Client Invoice</td><td className="text-end"><strong>$72,000</strong></td></tr>
                            <tr><td>Pay for 0-500 hrs</td><td className="text-end">$17,500</td></tr>
                            <tr><td>Pay for 500-600 hrs</td><td className="text-end">$7,000</td></tr>
                            <tr className="table-info"><td>Total Consultant Pay</td><td className="text-end"><strong>$24,500</strong></td></tr>
                            <tr className="table-success"><td>Caliber Profit</td><td className="text-end"><strong>$47,500</strong></td></tr>
                          </tbody>
                        </table>
                      </div>
                      <small className="text-muted">Calculation: (500×$35) + (100×$100×70%) = $24,500</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="alert alert-info mt-3">
                <i className="fas fa-lightbulb me-2"></i>
                <strong>Choose the Right Condition:</strong>
                <ul className="mb-0 mt-2">
                  <li><strong>Percentage-Based:</strong> Best for consistent margin rates regardless of consultant's pay scale</li>
                  <li><strong>Flat Rate:</strong> Ideal for standardized overhead costs and simple calculations</li>
                  <li><strong>Mixed:</strong> Perfect for combining fixed costs with performance-based margins</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Workflow Steps */}
          <div className="card mb-4">
            <div className="card-header bg-secondary text-white">
              <h3 className="mb-0">Monthly Workflow</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-3">
                  <div className="text-center">
                    <div className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                      <strong>1</strong>
                    </div>
                    <h6 className="mt-2">Setup</h6>
                    <p className="small">Create employees and assign payroll conditions</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="text-center">
                    <div className="bg-info text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                      <strong>2</strong>
                    </div>
                    <h6 className="mt-2">Timesheet</h6>
                    <p className="small">Employee submits monthly hours worked</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="text-center">
                    <div className="bg-warning text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                      <strong>3</strong>
                    </div>
                    <h6 className="mt-2">Calculate</h6>
                    <p className="small">System auto-calculates based on assigned condition</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="text-center">
                    <div className="bg-success text-white rounded-circle d-inline-flex align-items-center justify-content-center" style={{width: '50px', height: '50px'}}>
                      <strong>4</strong>
                    </div>
                    <h6 className="mt-2">Invoice</h6>
                    <p className="small">Generate invoice and track payment status</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* System Capabilities */}
          <div className="card mb-4">
            <div className="card-header bg-success text-white">
              <h3 className="mb-0">Complete System Capabilities</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h5 className="text-success">✅ All Calculation Types Supported</h5>
                  <ul>
                    <li><strong>Flat Rates:</strong> $50/hour, $75/hour, etc.</li>
                    <li><strong>Percentage Rates:</strong> 70%, 75%, 80% of client rate</li>
                    <li><strong>Tiered Structures:</strong> Different rates for different hour ranges</li>
                    <li><strong>Hike Amounts:</strong> Extra $5/hour after thresholds</li>
                    <li><strong>Bonus Systems:</strong> Performance-based bonuses</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h5 className="text-primary">🚀 Advanced Features</h5>
                  <ul>
                    <li><strong>Automated Calculations:</strong> No manual errors</li>
                    <li><strong>Historical Tracking:</strong> Complete audit trail</li>
                    <li><strong>Flexible Changes:</strong> Update conditions anytime</li>
                    <li><strong>Real-time Reports:</strong> Instant analytics</li>
                    <li><strong>Professional Interface:</strong> Modern web app</li>
                  </ul>
                </div>
              </div>
              
              <div className="alert alert-info mt-3">
                <i className="fas fa-lightbulb me-2"></i>
                <strong>Best of Both Worlds:</strong> The system combines the simplicity of flat rates with the flexibility of percentage-based calculations, plus advanced features like tiered structures and automatic bonuses.
              </div>
            </div>
          </div>

          {/* Quick Start Guide */}
          <div className="card">
            <div className="card-header bg-primary text-white">
              <h3 className="mb-0">Quick Start Guide</h3>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h5>🚀 Setup Steps</h5>
                  <ol>
                    <li><strong>Create Payroll Conditions:</strong> Choose between flat rates or percentage-based margins</li>
                    <li><strong>Add Employees:</strong> Create profiles with client/consultant rates</li>
                    <li><strong>Assign Conditions:</strong> Link each employee to a payroll condition</li>
                    <li><strong>Generate Invoices:</strong> System auto-calculates based on hours and conditions</li>
                    <li><strong>Track Performance:</strong> Use reports for insights and analytics</li>
                  </ol>
                </div>
                <div className="col-md-6">
                  <h5>💡 Pro Tips</h5>
                  <ul>
                    <li><strong>Start Simple:</strong> Begin with flat rates, then add complexity</li>
                    <li><strong>Use Tiers:</strong> Set different rates for different experience levels</li>
                    <li><strong>Track History:</strong> Change conditions anytime - system tracks everything</li>
                    <li><strong>Monitor Reports:</strong> Check condition effectiveness regularly</li>
                    <li><strong>Test Calculations:</strong> Use the preview feature before creating invoices</li>
                  </ul>
                </div>
              </div>
              
              <div className="alert alert-success mt-3">
                <i className="fas fa-check-circle me-2"></i>
                <strong>Ready to Go:</strong> The system is pre-loaded with 14 employees and 91 invoices from your Excel data. You can start using it immediately or modify the conditions as needed.
              </div>
            </div>
          </div>
    </div>
  );
};

export default HowItWorks;